import { SortOptions } from "@/shared/components/table/GenericTable";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AdminLeadControllerGetAllQueryParams, Nullable } from "service";
import { useDebounce } from "ui";

// Derived from the generated API client so it tracks the backend spec
// (regenerated via `yarn generate:api`) rather than drifting.
export type LeadSortField = NonNullable<
  AdminLeadControllerGetAllQueryParams["sort"]
>;

export interface FilterState {
  page: number;
  search: string;
  limit: number;
  sortOptions: Nullable<SortOptions>;
  selectedLeadIds: string[];
}

export type UpdateFilter = <K extends keyof FilterState>(
  key: K,
  value: FilterState[K] | ((prev: FilterState) => FilterState),
) => void;

const DEFAULT_LIMIT = 50;

const useLeadsFilters = () => {
  const { debounce } = useDebounce(475);
  const [params, setSearchParams] = useSearchParams();

  const [filters, setFilters] = useState<FilterState>({
    page: params.get("page") ? parseInt(params.get("page") as string) : 1,
    search: params.get("search") || "",
    limit: params.get("limit")
      ? parseInt(params.get("limit") as string)
      : DEFAULT_LIMIT,
    sortOptions: params.get("sortBy")
      ? {
          sortBy: params.get("sortBy") || "",
          direction: (params.get("direction") || "") as "asc" | "desc",
        }
      : null,
    selectedLeadIds: [],
  });

  const updateFilter: UpdateFilter = (key, value) => {
    setFilters((prev) =>
      typeof value === "function" ? value(prev) : { ...prev, [key]: value },
    );
  };

  const onSearch = (search: string) => {
    debounce(() => updateFilter("search", search));
  };

  const queryParams: AdminLeadControllerGetAllQueryParams = {
    ...(filters.page && { page: filters.page }),
    ...(filters.limit && { limit: filters.limit }),
    ...(filters.search && { search: filters.search }),
    ...(filters.sortOptions?.sortBy && {
      sort: filters.sortOptions.sortBy as LeadSortField,
    }),
    ...(filters.sortOptions?.direction && {
      order: filters.sortOptions.direction,
    }),
  };

  return { filters, updateFilter, queryParams, onSearch, setSearchParams };
};

export default useLeadsFilters;
